import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChannels, useGuildConfig } from '../../../lib/auditQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { UserPicker } from '../../../components/UserPicker.js';
import { MemberLabel } from '../../../components/MemberLabel.js';
import { ChannelPicker } from '../../../components/ChannelPicker.js';
import {
  usePurgeLogs,
  useStartPurge,
  type PurgeLogEntry,
  type PurgeStartResult,
} from '../../../lib/purgeQueries.js';
import { showToast } from '../../../lib/toast.js';

export const Route = createFileRoute('/guilds/$guildId/purge')({
  component: PurgePage,
});

const SNOWFLAKE_RE = /^\d{17,20}$/;
const COUNT_MIN = 1;
const COUNT_MAX = 1000;

function PurgePage() {
  const { guildId } = Route.useParams();

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-6xl space-y-8 px-6 py-8">
        <PurgeForm guildId={guildId} />
        <HistoryTable guildId={guildId} />
      </main>
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const guildConfig = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            {
              label: guildConfig.data?.name ?? guildId,
              to: '/guilds/$guildId',
              params: { guildId },
            },
            { label: t('purge.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function PurgeForm({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const channels = useChannels(guildId);
  const start = useStartPurge(guildId);

  const [channelId, setChannelId] = useState('');
  const [count, setCount] = useState('50');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterContains, setFilterContains] = useState('');
  const [olderThan14d, setOlderThan14d] = useState(false);

  const channelName = channels.data?.find((c) => c.id === channelId)?.name ?? '?';

  const countNum = Number(count);
  const countValid = Number.isInteger(countNum) && countNum >= COUNT_MIN && countNum <= COUNT_MAX;
  const userValid = filterUserId === '' || SNOWFLAKE_RE.test(filterUserId);
  const ready = channelId !== '' && countValid && userValid;

  const onSubmit = (): void => {
    if (!ready) return;
    if (!window.confirm(t('purge.confirm', { count: countNum, channel: channelName }))) {
      return;
    }
    const body = {
      channelId,
      count: countNum,
      olderThan14d,
      ...(filterUserId !== '' ? { filterUserId } : {}),
      ...(filterContains !== '' ? { filterContains } : {}),
    };
    start.mutate(body, {
      onSuccess: (res: PurgeStartResult) => {
        showToast(
          'success',
          t('purge.result', {
            deleted: res.bulkDeleted,
            queued: res.slowPurgeQueued,
            skipped: res.skippedOld,
          }),
        );
        setFilterContains('');
      },
    });
  };

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
        {t('purge.section.trigger')}
      </h2>
      <div className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('purge.field.channel')}>
            <ChannelPicker
              guildId={guildId}
              value={channelId}
              onChange={setChannelId}
              placeholder={t('purge.field.channelPick')}
            />
          </Field>

          <Field label={t('purge.field.count')} hint={t('purge.field.countHint')}>
            <input
              type="number"
              min={COUNT_MIN}
              max={COUNT_MAX}
              className="w-32 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm"
              value={count}
              onChange={(e) => {
                setCount(e.target.value);
              }}
            />
          </Field>

          <Field label={t('purge.field.filterUser')} hint={t('purge.field.filterUserHint')}>
            <UserPicker guildId={guildId} value={filterUserId} onChange={setFilterUserId} />
          </Field>

          <Field label={t('purge.field.filterContains')} hint={t('purge.field.filterContainsHint')}>
            <input
              type="text"
              maxLength={200}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm"
              value={filterContains}
              onChange={(e) => {
                setFilterContains(e.target.value);
              }}
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
            checked={olderThan14d}
            onChange={(e) => {
              setOlderThan14d(e.target.checked);
            }}
          />
          <span>{t('purge.field.olderThan14d')}</span>
        </label>
        <p className="-mt-2 text-xs text-zinc-500">{t('purge.field.olderThan14dHint')}</p>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!ready || start.isPending}
            className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-50"
          >
            {start.isPending ? t('common.loading') : t('purge.button.run')}
          </button>
        </div>

        {!countValid && count !== '' ? (
          <p className="text-xs text-rose-400">
            {t('purge.field.countError', { min: COUNT_MIN, max: COUNT_MAX })}
          </p>
        ) : null}
        {!userValid ? <p className="text-xs text-rose-400">{t('purge.field.userError')}</p> : null}
      </div>
    </section>
  );
}

function HistoryTable({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const channels = useChannels(guildId);
  const logs = usePurgeLogs(guildId);

  const channelName = (id: string): string => channels.data?.find((c) => c.id === id)?.name ?? id;

  const rows: PurgeLogEntry[] = logs.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <section>
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">
          {t('purge.section.history')}
        </h2>
        <button
          type="button"
          onClick={() => {
            void logs.refetch();
          }}
          disabled={logs.isFetching}
          className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
        >
          {t('purge.button.refresh')}
        </button>
      </header>

      {logs.isLoading ? (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
          {t('common.loading')}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
          {t('purge.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-xs uppercase tracking-wider text-zinc-400">
              <tr>
                <th className="px-3 py-2 text-left">{t('purge.table.date')}</th>
                <th className="px-3 py-2 text-left">{t('purge.table.channel')}</th>
                <th className="px-3 py-2 text-left">{t('purge.table.moderator')}</th>
                <th className="px-3 py-2 text-left">{t('purge.table.filter')}</th>
                <th className="px-3 py-2 text-right">{t('purge.table.requested')}</th>
                <th className="px-3 py-2 text-right">{t('purge.table.deleted')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-zinc-800">
                  <td className="px-3 py-2 text-xs text-zinc-400">
                    {new Date(r.createdAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">#{channelName(r.channelId)}</td>
                  <td className="px-3 py-2">
                    <MemberLabel guildId={guildId} userId={r.moderatorId} compact />
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-400">
                    {r.filterUserId ? (
                      <MemberLabel guildId={guildId} userId={r.filterUserId} compact />
                    ) : r.filterContains ? (
                      `contains="${r.filterContains}"`
                    ) : (
                      t('purge.table.filterNone')
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.requestedCount}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.deletedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 flex justify-center">
        {logs.hasNextPage ? (
          <button
            type="button"
            disabled={logs.isFetchingNextPage}
            onClick={() => {
              void logs.fetchNextPage();
            }}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-1.5 text-sm hover:bg-zinc-800 disabled:opacity-50"
          >
            {logs.isFetchingNextPage ? t('common.loadingMore') : t('common.loadMore')}
          </button>
        ) : rows.length > 0 ? (
          <span className="text-xs text-zinc-500">{t('common.endOfList')}</span>
        ) : null}
      </div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}
